import React from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAppStore } from "../../../appStore";
import { useAnalyticsStore } from "./AnalyticsStore/analyticsStore";
import { AlertState, UserFilters } from "./AnalyticsUtils/analyticsInterfaces";
import { enUS, enGB } from "date-fns/locale";
import { updateSearchParamValues } from "../../utils/updateSearchParamValues";

export const EndDateDropDown = ({
  searchParams,
  setSearchParams,
}: {
  searchParams: URLSearchParams;
  setSearchParams: (searchParams: URLSearchParams) => void;
}) => {
  const {
    userFilters,
    setUserFilters,
    setAlertState,
    setShowMoreVisible,
    setShowMoreOpen,
    setDateChangesMade,
  } = useAnalyticsStore();

  const [endTimeAnchorEl, setEndTimeAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const endTimeOpen = Boolean(endTimeAnchorEl);
  const handleEndTimeClick = (event: React.MouseEvent<HTMLElement>) => {
    setEndTimeAnchorEl(event.currentTarget);
  };
  const handleEndTimeClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setEndTimeAnchorEl(null);
  };

  const { dateMMDDYYYY, setDateMMDDYYYY } = useAppStore();

  const selectedLocale = dateMMDDYYYY ? enUS : enGB;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={selectedLocale}
    >
      <DatePicker
        label={
          userFilters.end_date ? (
            <div className="flex">
              <>End Date</>
              <div>
                <IconButton
                  aria-label="more"
                  id="long-button"
                  aria-controls={endTimeOpen ? "long-menu" : undefined}
                  aria-expanded={endTimeOpen ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={handleEndTimeClick}
                  sx={{
                    marginLeft: "5px",
                    padding: 0,
                    color: "#000000",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="long-menu"
                  MenuListProps={{
                    "aria-labelledby": "long-button",
                  }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  anchorEl={endTimeAnchorEl}
                  open={endTimeOpen}
                  onClose={handleEndTimeClose}
                  slotProps={{
                    paper: {
                      style: {
                        maxHeight: 48 * 4.5,
                        width: "20ch",
                      },
                    },
                  }}
                >
                  <MenuItem
                    key="mm-dd-yyyy-end"
                    onClick={(event) => {
                      setDateMMDDYYYY(true);
                      handleEndTimeClose(event);
                    }}
                  >
                    MM/DD/YYYY
                  </MenuItem>
                  <MenuItem
                    key="dd-mm-yyyy-end"
                    onClick={(event) => {
                      setDateMMDDYYYY(false);
                      handleEndTimeClose(event);
                    }}
                  >
                    DD/MM/YYYY
                  </MenuItem>
                </Menu>
              </div>
            </div>
          ) : (
            <>End Date</>
          )
        }
        value={userFilters.end_date}
        onChange={(newValue) => {
          if (newValue === null) {
            setUserFilters((previousState: UserFilters) => ({
              ...previousState,
              end_date: new Date(),
            }));
            updateSearchParamValues(
              {
                ...userFilters,
                end_date: new Date(),
              },
              searchParams,
              setSearchParams,
              ""
            );
            return;
          }
          setDateChangesMade(true);
          setShowMoreVisible(false);
          setShowMoreOpen(false);
          if (localStorage.filtersSavedClicked === "true") {
            localStorage.setItem(
              "savedAnalyticsFilters",
              JSON.stringify({
                parent_group: userFilters.parent_group,
                site: userFilters.site,
                camera: userFilters.camera,
                start_date: userFilters.start_date,
                end_date: newValue,
              })
            );
          }
          setAlertState({
            dateTooFar: false,
            startAfterEnd: false,
            endBeforeStart: false,
          });

          if (
            newValue !== null &&
            (newValue > userFilters.start_date ||
              newValue.toString() === userFilters.start_date.toString())
          ) {
            setUserFilters((previousState: UserFilters) => ({
              ...previousState,
              end_date: newValue,
            }));
            updateSearchParamValues(
              {
                ...userFilters,
                end_date: newValue,
              },
              searchParams,
              setSearchParams,
              ""
            );
          }
          if (
            newValue !== null &&
            newValue < userFilters.start_date &&
            newValue.toString() !== userFilters.start_date.toString() &&
            userFilters.start_date !== null
          ) {
            setAlertState({
              dateTooFar: false,
              startAfterEnd: false,
              endBeforeStart: true,
            });
          }
        }}
        renderInput={(params) => (
          <TextField sx={{ width: 150 }} helperText="*Required" {...params} />
        )}
      />
    </LocalizationProvider>
  );
};
